'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { getMainVideo, getWebsiteTextContent } from '@/src/lib/sanity.queries';
import Spinner from './Spinner';
import { cn } from '@/lib/utils';

type WebsiteTextContent = {
  videoSectionTitle: string;
  videoSectionDesc: string;
};

type MainVideo = {
  videoUrl: string;
  videoUrlMobile: string;
};

export default function VideoSection({ locale = 'en' }: { locale?: 'en' | 'ar' | string }) {
  const t = useTranslations('Home.VideoSection');
  const videoRef = useRef<any>(null);

  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [content, setContent] = useState<WebsiteTextContent | null>(null);
  const [mainVideo, setMainVideo] = useState<MainVideo | null>(null);

  // Video event handlers
  const handleVideoLoadStart = useCallback(() => {
    setIsVideoLoading(true);
    setIsVideoReady(false);
  }, []);

  const handleVideoCanPlay = useCallback(() => {
    setIsVideoLoading(false);
    setIsVideoReady(true);
  }, []);

  const handleVideoError = useCallback(() => {
    setIsVideoLoading(false);
    setVideoError(true);
  }, []);

  // Fetch data from Sanity
  useEffect(() => {
    async function fetchData() {
      try {
        const [videoData, contentData] = await Promise.all([
          getMainVideo(),
          getWebsiteTextContent(locale),
        ]);

        setMainVideo(videoData);
        setContent({
          videoSectionTitle: contentData?.videoSectionTitle ?? '',
          videoSectionDesc: contentData?.videoSectionDesc ?? '',
        });
      } catch (error) {
        console.error('Failed to fetch main video or text content:', error);
        setVideoError(true);
      }
    }

    fetchData();
  }, [locale]);

  // Attach listeners once per mount
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.addEventListener('loadstart', handleVideoLoadStart);
    video.addEventListener('canplay', handleVideoCanPlay);
    video.addEventListener('error', handleVideoError);
    return () => {
      video.removeEventListener('loadstart', handleVideoLoadStart);
      video.removeEventListener('canplay', handleVideoCanPlay);
      video.removeEventListener('error', handleVideoError);
    };
  }, [handleVideoLoadStart, handleVideoCanPlay, handleVideoError]);

  // Reload and try autoplay whenever the source changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const hasSrc = Boolean(mainVideo?.videoUrl || mainVideo?.videoUrlMobile);
    if (!hasSrc) return;
    setIsVideoLoading(true);
    setIsVideoReady(false);
    setVideoError(false);
    video.load();
    const tryPlay = async () => {
      try {
        await video.play();
      } catch {
        // Autoplay may be blocked; rely on user interaction
      }
    };
    void tryPlay();
  }, [mainVideo?.videoUrl, mainVideo?.videoUrlMobile]);

  const isRTL = locale === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';

  return (
    <section
      id="Home"
      dir={dir}
      className={cn(
        'relative mx-auto flex h-full w-full max-w-[1440px] flex-col items-center justify-center overflow-hidden rounded-4xl p-2 text-white',
        'mb-10 transition-all duration-300 ease-in-out'
      )}>
      {/* Video Container */}
      <div className="relative max-h-[700px] min-h-[400px] w-full">
        <video
          key={mainVideo?.videoUrl || mainVideo?.videoUrlMobile || 'fallback'}
          ref={videoRef}
          className="h-full max-h-[700px] min-h-[400px] w-full rounded-4xl object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onLoadStart={handleVideoLoadStart}
          onCanPlay={handleVideoCanPlay}
          onError={handleVideoError}>
          {mainVideo?.videoUrlMobile && (
            <source src={mainVideo.videoUrlMobile} type="video/mp4" media="(max-width: 768px)" />
          )}
          <source src={mainVideo?.videoUrl || '/NOB.mp4'} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Loading Overlay */}
        {isVideoLoading && !videoError && (
          <div className="absolute inset-0 flex max-h-[700px] min-h-[400px] items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
              <Spinner />
              <p className="text-sm text-white/80">Loading video...</p>
            </div>
          </div>
        )}
      </div>

      {/* Content Overlay */}
      {(isVideoReady || videoError) && (
        <section
          className={cn(
            'bg-primary-500 right-4 bottom-4 left-4 mt-4 flex flex-col items-center justify-center gap-3 rounded-4xl px-2.5 py-5',
            'transition-all duration-500 ease-in-out',
            'animate-in fade-in-0 slide-in-from-bottom-2'
          )}>
          <h1 className="text-center text-xl font-semibold text-white sm:text-2xl lg:text-3xl">
            {content?.videoSectionTitle || t('Title')}
          </h1>
          <p className="text-center text-sm text-white/90 sm:text-base lg:text-lg">
            {content?.videoSectionDesc || t('Desc')}
          </p>
        </section>
      )}
    </section>
  );
}
